import { useEffect, useRef, useState } from "react"
import {
  ExclamationCircleFilled,
  SafetyCertificateOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import { App, Button, Flex, Form, Input, Typography } from "antd"
import { Alert } from "~/components/Alert"
import {
  confirmUnusedOrderRefund,
  deleteUnusedLevelOrder,
  deleteUnusedWalletOrder,
  getStaffOrderCleanupDetail,
  refundUnusedOrder,
} from "~/api/staff"
import {
  CleanupGuidance,
  OrderDetails,
  PaymentDetails,
  RechargeDetails,
  RefundDetails,
} from "./Details"
import { CleanupActions, type CleanupAction, WorkflowStatus } from "./Workflow"
import { formatAmount, isAbortError } from "~/common/utils"
import styles from "./index.css"

const { Text, Title } = Typography

type CleanupFormValues = {
  orderNo: string
}

export const StaffOrderCleanup = () => {
  const [form] = Form.useForm<CleanupFormValues>()
  const { message, modal } = App.useApp()
  const queryControllerRef = useRef<AbortController | undefined>(undefined)
  const [detail, setDetail] =
    useState<API.Staff.OrderCleanupDetailResponse | null>(null)
  const [deletedResult, setDeletedResult] =
    useState<API.Staff.DeleteUnusedOrderResult | null>(null)
  const [querying, setQuerying] = useState(false)
  const [activeAction, setActiveAction] = useState<CleanupAction>(null)

  useEffect(() => {
    return () => queryControllerRef.current?.abort()
  }, [])

  const resetResult = () => {
    queryControllerRef.current?.abort()
    setDetail(null)
    setDeletedResult(null)
  }

  const refreshOrderDetail = async (orderNo: string) => {
    const response = await getStaffOrderCleanupDetail({ order_no: orderNo })
    if (!response.data) {
      throw new Error("订单清理信息为空")
    }
    setDetail(response.data)
  }

  const refreshAfterAction = async (orderNo: string) => {
    try {
      await refreshOrderDetail(orderNo)
    } catch {
      setDetail(null)
      void message.warning("操作已完成，但状态刷新失败，请重新查询该订单")
    }
  }

  const queryOrder = async () => {
    let values: CleanupFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    const controller = new AbortController()
    queryControllerRef.current?.abort()
    queryControllerRef.current = controller
    setQuerying(true)
    setDetail(null)
    setDeletedResult(null)

    try {
      const response = await getStaffOrderCleanupDetail(
        { order_no: values.orderNo.trim() },
        controller.signal,
      )
      if (!response.data) {
        void message.error("订单清理信息为空，请稍后重试")
        return
      }
      setDetail(response.data)
    } catch (error) {
      if (!isAbortError(error)) {
        setDetail(null)
      }
    } finally {
      if (queryControllerRef.current === controller) {
        setQuerying(false)
      }
    }
  }

  const requestRefund = async (orderNo: string) => {
    setActiveAction("refund")
    try {
      const response = await refundUnusedOrder({ order_no: orderNo })
      if (!response.data) {
        void message.error("退款申请结果为空，请重新查询订单")
        throw new Error("退款申请结果为空")
      }
      void message.success(
        response.data.status === "SUCCESS"
          ? "微信退款申请成功，请人工核实到账"
          : "微信退款申请已受理，请稍后人工核实到账",
      )
      await refreshAfterAction(orderNo)
    } finally {
      setActiveAction(null)
    }
  }

  const confirmRefund = async (orderNo: string) => {
    if (
      detail?.order.order_no !== orderNo ||
      !detail.available_actions.can_confirm_refund
    )
      return

    setActiveAction("confirm")
    try {
      const response = await confirmUnusedOrderRefund({ order_no: orderNo })
      if (!response.data) {
        void message.error("确认退款结果为空，请重新查询订单")
        throw new Error("确认退款结果为空")
      }
      void message.success(`订单 ${orderNo} 已确认退款到账`)
      await refreshAfterAction(orderNo)
    } finally {
      setActiveAction(null)
    }
  }

  const deleteOrder = async (
    orderNo: string,
    cleanupType: API.Staff.OrderCleanupType,
  ) => {
    setActiveAction("delete")
    try {
      const response =
        cleanupType === "wallet"
          ? await deleteUnusedWalletOrder({ order_no: orderNo })
          : await deleteUnusedLevelOrder({ order_no: orderNo })

      if (!response.data) {
        void message.error("订单删除结果为空，请重新查询")
        throw new Error("订单删除结果为空")
      }

      setDeletedResult(response.data)
      setDetail(null)
      form.resetFields()
      void message.success(`测试订单 ${orderNo} 已删除`)
    } finally {
      setActiveAction(null)
    }
  }

  const confirmRequestRefund = () => {
    if (!detail?.available_actions.can_refund) return

    const orderNo = detail.order.order_no
    modal.confirm({
      title: (
        <Flex
          align="center"
          gap={12}
        >
          <ExclamationCircleFilled className={styles.confirmTitleIcon} />
          <span>确认发起微信退款？</span>
        </Flex>
      ),
      icon: null,
      width: 560,
      content: (
        <Flex
          className={styles.confirmContent}
          gap={12}
          vertical
        >
          <Text>订单号：{orderNo}</Text>
          <Text>退款金额：{formatAmount(detail.order.amount)}</Text>
          <Alert
            description="退款申请提交后不会立即删除任何业务数据。请等待用户实际收到退款，再单独执行人工确认。"
            showIcon
            title="将按原支付渠道发起全额退款"
          />
        </Flex>
      ),
      okButtonProps: { danger: true },
      okText: "确认发起退款",
      cancelText: "暂不退款",
      onOk: () => requestRefund(orderNo),
    })
  }

  const confirmDelete = () => {
    if (
      !detail?.can_delete ||
      !detail.available_actions.can_delete ||
      !detail.cleanup_type
    )
      return

    const orderNo = detail.order.order_no
    const cleanupType = detail.cleanup_type
    const actionLabel =
      cleanupType === "wallet" ? "删除积点订单" : "删除专业版订单"
    modal.confirm({
      title: (
        <Flex
          align="center"
          gap={12}
        >
          <ExclamationCircleFilled className={styles.confirmTitleIcon} />
          <span>{`确认${actionLabel}？`}</span>
        </Flex>
      ),
      icon: null,
      width: 560,
      content: (
        <Flex
          className={styles.confirmContent}
          gap={12}
          vertical
        >
          <Text>订单号：{orderNo}</Text>
          <Alert
            description="该订单已经由 Staff 人工确认退款到账。删除后订单、支付记录及关联充值记录将无法恢复。"
            showIcon
            title="请最后核对订单号和关联数据"
            level="strong"
          />
        </Flex>
      ),
      okButtonProps: { danger: true },
      okText: "永久删除订单",
      cancelText: "暂不删除",
      onOk: () => deleteOrder(orderNo, cleanupType),
    })
  }

  const actionBusy = activeAction !== null

  return (
    <Flex
      className={styles.page}
      gap={18}
      vertical
    >
      <Flex
        gap={6}
        vertical
      >
        <Title level={2}>测试订单退款与清理</Title>
        <Text type="secondary">
          按照退款、人工确认到账、删除的顺序处理 Staff 测试订单。
        </Text>
      </Flex>

      <Alert
        description="必须先由系统发起微信退款，再由 Staff 人工核实并确认退款到账，最后才可删除。Staff 人工录入订单、非测试账号订单以及权益已使用的订单均不可操作。"
        showIcon
        title="只能删除 Staff 账号的在线充值订单"
      />

      <CleanupGuidance />

      <section className={styles.queryPanel}>
        <Flex
          align="center"
          gap={10}
        >
          <SafetyCertificateOutlined className={styles.sectionIcon} />
          <Title level={4}>查询订单</Title>
        </Flex>
        <Form<CleanupFormValues>
          className={styles.queryForm}
          form={form}
          onFinish={() => void queryOrder()}
        >
          <Flex gap={12}>
            <Form.Item
              className={styles.orderInput}
              name="orderNo"
              rules={[
                { required: true, message: "请输入订单号" },
                { max: 32, message: "订单号不能超过 32 个字符" },
              ]}
            >
              <Input
                allowClear
                disabled={actionBusy}
                placeholder="请输入需要退款或清理的订单 order_no"
                size="large"
                onChange={resetResult}
              />
            </Form.Item>
            <Button
              htmlType="submit"
              icon={<SearchOutlined />}
              disabled={actionBusy}
              loading={querying}
              size="large"
              type="primary"
            >
              查询订单
            </Button>
          </Flex>
        </Form>
      </section>

      {deletedResult ? (
        <Alert
          description={`已删除订单记录 ${deletedResult.deleted_order_id}、支付记录 ${deletedResult.deleted_payment_trade_id} 和 ${deletedResult.deleted_wallet_recharge_record_ids.length} 条积点充值记录。`}
          showIcon
          title={`测试订单 ${deletedResult.order_no} 删除成功`}
          type="success"
        />
      ) : null}

      {detail ? (
        <section className={styles.detailPanel}>
          <WorkflowStatus detail={detail} />

          <OrderDetails order={detail.order} />
          <PaymentDetails payment={detail.payment_trade} />
          <RechargeDetails records={detail.wallet_recharge_records} />
          <RefundDetails refund={detail.refund} />

          <CleanupActions
            activeAction={activeAction}
            detail={detail}
            onConfirmRefund={() => confirmRefund(detail.order.order_no)}
            onDelete={confirmDelete}
            onRequestRefund={confirmRequestRefund}
          />
        </section>
      ) : null}
    </Flex>
  )
}
